import { missionCopy } from "./mission-copy";
/**
 * Inside the laptop's screen: the briefing, as a strike and a counterstrike.
 *
 * Night: the Enemy's strike writes itself in, word by word. Then they hold
 * the light to charge it and let go: a shot flies into the Enemy's words,
 * throws them apart, and day floods out from the hit. Day, in two pages:
 * their counterstrike (the mission) with "Show me examples", then how other
 * agents beat this strike, as cards, with the accept (or Done) button.
 *
 * Laid out in its own scoped CSS (`.smcs`): the phases are classes on the
 * root, and the hold, the shot and the flood are driven per frame through
 * custom properties, which Tailwind classes cannot follow.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type {
  Mission,
  MissionExample,
} from "@/features/missionkit/api/use-mission";
import type { Tier } from "@/features/funnel/types";
import { paletteVars, parseStrike } from "./open-case";
import { EnemyMark } from "./strike-page";

/** How hard each case hits: the harder the mission, the longer the hold,
 *  the more the screen shakes as it charges, and the bigger the impact
 *  (flash, shockwaves, sparks, quake) and the slower the flood after it. */
const FX: Record<
  Tier,
  {
    hold: number;
    rattle: number;
    shake: number;
    glow: number;
    vignette: number;
    push: number;
    flash: number;
    rings: number;
    sparks: number;
    quake: number;
    flood: number;
  }
> = {
  beginner: {
    hold: 1500,
    rattle: 1,
    shake: 0,
    glow: 1,
    vignette: 0,
    push: 1,
    flash: 0,
    rings: 0,
    sparks: 0,
    quake: 0,
    flood: 640,
  },
  challenging: {
    hold: 2000,
    rattle: 1.8,
    shake: 2.5,
    glow: 1.35,
    vignette: 0,
    push: 1.6,
    flash: 0.75,
    rings: 1,
    sparks: 0,
    quake: 6,
    flood: 820,
  },
  improbable: {
    hold: 2600,
    rattle: 2.8,
    shake: 6,
    glow: 1.8,
    vignette: 0.75,
    push: 2.4,
    flash: 1,
    rings: 3,
    sparks: 18,
    quake: 14,
    flood: 1050,
  },
};
/** How long the mission has the screen to itself before the button comes. */
const BUTTON_DELAY = 1300;

type Word = { w: string; d: number };
/** One entry per word, each with its own delay; and when the last lands. */
function words(text: string, start: number, step: number, pause: number) {
  const out: Word[] = [];
  let t = start;
  const sentences = text.match(/[^.?!]+[.?!]+[”’"']*\s*/g);
  const parts =
    sentences && sentences.join("").trim().length === text.trim().length
      ? sentences
      : [text];
  for (const s of parts) {
    for (const w of s.trim().split(/\s+/)) {
      out.push({ w, d: t });
      t += step;
    }
    t += pause;
  }
  return { list: out, end: t - pause };
}

/** A random shake, up to `amp` either way. */
const jitter = (amp: number) => ((Math.random() - 0.5) * 2 * amp).toFixed(2);

/** "Operative M." -> "Agent M." */
const agentName = (agent: string) => agent.replace(/^Operative /, "Agent ");

const buzz = (p: number | number[]) => {
  try {
    navigator.vibrate?.(p);
  } catch {
    /* no vibration here */
  }
};

type FieldReport = MissionExample;
const isVideo = (src: string) => /\.(mp4|webm|mov)(\?|$)/i.test(src);

/** One agent's report, compact: their words cut to three lines, a small
 *  thumbnail of what they sent, and "Show more" when there is more to see
 *  (the words ran over, or there is a photo or video to see properly). */
function ExampleCard({
  report,
  index,
  onOpen,
}: {
  report: FieldReport;
  index: number;
  onOpen: () => void;
}) {
  const text = useRef<HTMLParagraphElement>(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const el = text.current;
    if (el) setClipped(el.scrollHeight > el.clientHeight + 1);
  }, [report.did]);
  return (
    <article className="ex-card" style={{ "--i": index } as CSSProperties}>
      <div className="ex-row">
        <div className="ex-body">
          <p className="ex-who">{agentName(report.agent)}</p>
          <p ref={text} className="ex-text">
            {report.did}
          </p>
        </div>
        {report.media ? (
          <button
            type="button"
            className="ex-thumb"
            aria-label={`See what ${agentName(report.agent)} sent`}
            onClick={onOpen}
          >
            {isVideo(report.media) ? (
              <video src={report.media} muted loop autoPlay playsInline />
            ) : (
              <img src={report.media} alt="" />
            )}
          </button>
        ) : null}
      </div>
      {clipped || report.media || report.felt ? (
        <button type="button" className="ex-more" onClick={onOpen}>
          Show more
        </button>
      ) : null}
    </article>
  );
}

export function Counterstrike({
  tier,
  mission,
  acceptLabel,
  onAccept,
  review = false,
  /** a repeat (missions filed already): the strike in half the time, a
   *  shorter hold; the first time, and Mission Improbable, keep the full one */
  quick = false,
  initialPage = "mission",
  onPageChange,
  strikeNotice,
}: {
  /** whose case: sets the colours */
  tier: Tier;
  mission: Mission;
  acceptLabel: string;
  onAccept: () => void;
  /** opened again from the live mission: straight to the day side (the
   *  mission and the tactics), the button there to go back */
  review?: boolean;
  quick?: boolean;
  initialPage?: "mission" | "examples";
  onPageChange?: (page: "mission" | "examples") => void;
  strikeNotice?: string;
}) {
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const reports = mission.examples ?? [];
  const strike = parseStrike(mission.strike).body;
  const [enemy] = useState(() =>
    quick ? words(strike, 500, 40, 250) : words(strike, 1300, 85, 650),
  );
  const { headline, detail } = missionCopy(mission);
  const long = headline.length > 200;
  const [mine] = useState(() =>
    words(headline, 450, long ? 20 : 45, long ? 120 : 260),
  );

  const root = useRef<HTMLDivElement>(null);
  const nightEl = useRef<HTMLElement>(null);
  const fx = quick ? { ...FX[tier], hold: FX[tier].hold / 2 } : FX[tier];
  const strikeEl = useRef<HTMLDivElement>(null);
  const lineEl = useRef<HTMLHeadingElement>(null);
  const holdEl = useRef<HTMLButtonElement>(null);
  const shotEl = useRef<HTMLSpanElement>(null);

  // the phases, as classes on the root
  const [ready, setReady] = useState(false);
  const [skip, setSkip] = useState(false);
  const [holding, setHolding] = useState(false);
  const [charged, setCharged] = useState(false);
  const [fired, setFired] = useState(false);
  const [impact, setImpact] = useState(false);
  const [lit, setLit] = useState(review);
  const [label, setLabel] = useState("Hold to counterstrike");
  // two pages: their strike and mission ("Show me examples"), then how other
  // agents countered it, as cards (accept, or Done when viewed again)
  const [page, setPage] = useState<"mission" | "examples">(initialPage);
  // the report open full screen, from "Show more"
  const [full, setFull] = useState<number | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const [dock, setDock] = useState(review);

  // per-frame state the loop reads (refs, so it never goes stale)
  const s = useRef({
    p: 0,
    holding: false,
    charged: false,
    fired: false,
    last: 0,
    raf: 0,
    autoFire: 0,
    tick: 0,
  });
  const timers = useRef<number[]>([]);
  const later = (fn: () => void, ms: number) =>
    timers.current.push(window.setTimeout(fn, reduce ? Math.min(ms, 60) : ms));
  useEffect(() => {
    if (!review) later(() => setReady(true), enemy.end + 600);
    const t = timers.current;
    const st = s.current;
    return () => {
      t.forEach(clearTimeout);
      cancelAnimationFrame(st.raf);
      clearTimeout(st.autoFire);
    };
    // once, on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setP = (p: number) =>
    root.current?.style.setProperty("--p", p.toFixed(4));

  const complete = () => {
    setLit(true);
    later(() => setDock(true), mine.end + 400 + BUTTON_DELAY);
  };

  const tick = (ts: number) => {
    const st = s.current;
    if (st.fired) {
      st.raf = 0;
      return;
    }
    if (!st.last) st.last = ts;
    const dt = Math.min(64, ts - st.last);
    st.last = ts;
    st.p = st.holding
      ? Math.min(1, st.p + dt / fx.hold)
      : Math.max(0, st.p - dt / 420);
    setP(st.p);
    // the Enemy's words rattle as the charge builds
    const amp = (st.charged ? 3 : st.p * st.p * 2.6) * fx.rattle;
    if (strikeEl.current)
      strikeEl.current.style.transform =
        amp > 0.05 ? `translate(${jitter(amp)}px,${jitter(amp)}px)` : "";
    // the harder cases shake the whole screen as it charges
    const quake = fx.shake * (st.charged ? 1 : st.p * st.p);
    if (nightEl.current)
      nightEl.current.style.transform =
        quake > 0.1 ? `translate(${jitter(quake)}px,${jitter(quake)}px)` : "";
    if (
      st.holding &&
      !st.charged &&
      st.p >= (st.tick + 1) * 0.25 &&
      st.tick < 3
    ) {
      st.tick++;
      buzz((8 + st.tick * 4) * fx.rattle);
    }
    if (st.holding && !st.charged && st.p >= 1) {
      st.charged = true;
      setCharged(true);
      setLabel("Let go to strike");
      buzz(35 * fx.rattle);
      // in case they never lift their thumb
      st.autoFire = window.setTimeout(fire, 1200);
    }
    st.raf = st.holding || st.p > 0 ? requestAnimationFrame(tick) : 0;
  };

  const startHold = () => {
    const st = s.current;
    if (!ready || st.holding || st.fired) return;
    if (reduce) {
      st.fired = true;
      setFired(true);
      complete();
      return;
    }
    st.holding = true;
    st.last = 0;
    setHolding(true);
    if (!st.charged) setLabel("Keep holding");
    if (!st.raf) st.raf = requestAnimationFrame(tick);
  };
  const endHold = () => {
    const st = s.current;
    if (!st.holding) return;
    st.holding = false;
    setHolding(false);
    if (st.charged) {
      fire();
      return;
    }
    setLabel(st.p > 0.12 ? "Not yet. Hold it down." : "Hold to counterstrike");
    st.tick = 0;
  };

  function fire() {
    const st = s.current;
    if (st.fired) return;
    st.fired = true;
    st.holding = false;
    clearTimeout(st.autoFire);
    cancelAnimationFrame(st.raf);
    st.raf = 0;
    const r = root.current;
    const hold = holdEl.current;
    const line = lineEl.current;
    const shot = shotEl.current;
    if (!r || !hold || !line || !shot) {
      complete();
      return;
    }
    if (strikeEl.current) strikeEl.current.style.transform = "";
    if (nightEl.current) nightEl.current.style.transform = "";
    setHolding(false);
    setCharged(false);
    // where the shot starts (the thumb) and lands (the middle of their line)
    const pr = r.getBoundingClientRect();
    const hr = hold.getBoundingClientRect();
    const er = line.getBoundingClientRect();
    const sx = hr.left + hr.width / 2 - pr.left;
    const sy = hr.top + hr.height / 2 - pr.top;
    const tx = er.left + er.width / 2 - pr.left;
    const ty = er.top + er.height / 2 - pr.top;
    r.style.setProperty("--sx", `${sx}px`);
    r.style.setProperty("--sy", `${sy}px`);
    r.style.setProperty("--cx", `${tx}px`);
    r.style.setProperty("--cy", `${ty}px`);
    const maxR =
      Math.hypot(Math.max(tx, pr.width - tx), Math.max(ty, pr.height - ty)) + 6;
    // each word is thrown its own way, away from the hit
    line.querySelectorAll<HTMLElement>(".w").forEach((w) => {
      const b = w.getBoundingClientRect();
      const dx = b.left + b.width / 2 - pr.left - tx;
      const dy = b.top + b.height / 2 - pr.top - ty;
      const d = Math.hypot(dx, dy) || 1;
      const push = (90 + Math.random() * 110) * fx.push;
      w.style.setProperty(
        "--bx",
        `${((dx / d) * push + (Math.random() - 0.5) * 40).toFixed(0)}px`,
      );
      w.style.setProperty(
        "--by",
        `${((dy / d) * push + (Math.random() - 0.5) * 40).toFixed(0)}px`,
      );
      w.style.setProperty(
        "--br",
        `${((Math.random() - 0.5) * 50).toFixed(0)}deg`,
      );
    });
    setFired(true);
    buzz(12);
    const flight = shot.animate(
      [
        { transform: "translate(-50%,-50%)" },
        {
          transform: `translate(-50%,-50%) translate(${tx - sx}px,${ty - sy}px)`,
        },
      ],
      { duration: 230, easing: "cubic-bezier(.55,0,.9,.45)", fill: "forwards" },
    );
    flight.onfinish = () => {
      setImpact(true);
      buzz(fx.quake ? [60, 30, 90, 30, 120] : [40, 30, 60]);
      const t0 = performance.now();
      const D = fx.flood;
      const step = (t: number) => {
        const k = Math.min(1, Math.max(0, (t - t0) / D));
        const e = 1 - Math.pow(1 - k, 3);
        r.style.setProperty("--r", `${(maxR * e).toFixed(1)}px`);
        if (k < 1) st.raf = requestAnimationFrame(step);
        else {
          st.raf = 0;
          complete();
        }
      };
      st.raf = requestAnimationFrame(step);
    };
  }

  const cls = [
    "smcs",
    "run",
    review && "review",
    skip && "skip",
    ready && "ready",
    holding && "holding",
    charged && "charged",
    fired && "fired settled",
    impact && "impact",
    lit && "lit",
    dock && "docked",
    page === "examples" && "examples-page",
  ]
    .filter(Boolean)
    .join(" ");
  // the original mock's sizes (spy15): 31px, 26px past 125 characters,
  // 19.5px past 200 — each a bit bigger here (34, 28.5, 21.5)
  const missionSize = long
    ? "21.5px"
    : headline.length > 125
      ? "28.5px"
      : "34px";
  const count = reports.length;

  return (
    <div
      ref={root}
      className={cls}
      style={
        {
          ...paletteVars(tier),
          "--fx-glow": fx.glow,
          "--fx-vignette": fx.vignette,
          "--fx-flash": fx.flash,
          "--fx-quake": `${fx.quake}px`,
        } as CSSProperties
      }
    >
      {/* night: the Enemy's strike */}
      <section
        ref={nightEl}
        inert={lit}
        className="night"
        onClick={(e) => {
          if (!ready && !(e.target as HTMLElement).closest(".hold")) {
            setSkip(true);
            setReady(true);
          }
        }}
      >
        {/* their seal, faint, behind their words */}
        <EnemyMark
          tone="screen"
          className="seal w-[78%] max-w-[340px] opacity-[.13]"
        />
        <div className="strike" ref={strikeEl}>
          <p className="tag tag-enemy">The Enemy&apos;s Strike</p>
          <h1
            ref={lineEl}
            className="enemy-line"
            style={
              {
                "--enemy-size": strike.length > 100 ? "29px" : "34px",
              } as CSSProperties
            }
          >
            {enemy.list.map((x, i) => (
              <span key={i}>
                <span
                  className="w"
                  style={{ "--d": `${x.d}ms` } as CSSProperties}
                >
                  {x.w}
                </span>{" "}
              </span>
            ))}
          </h1>
        </div>
        <div className="hold-zone">
          <button
            ref={holdEl}
            type="button"
            className="hold"
            aria-label="Hold down, then let go, to strike back and see your mission"
            onPointerDown={(e) => {
              e.preventDefault();
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                /* capture is a nicety */
              }
              startHold();
            }}
            onPointerUp={endHold}
            onPointerCancel={endHold}
            onLostPointerCapture={endHold}
            onContextMenu={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if ((e.key === " " || e.key === "Enter") && !e.repeat) {
                e.preventDefault();
                startHold();
              }
            }}
            onKeyUp={(e) => {
              if (e.key === " " || e.key === "Enter") endHold();
            }}
          >
            <span className="charge" />
            <svg className="cs-ring" viewBox="0 0 120 120" aria-hidden="true">
              <circle className="ring-bg" cx="60" cy="60" r="54" />
              <circle className="ring-fg" cx="60" cy="60" r="54" />
            </svg>
            <span className="pilot" />
          </button>
          <p className="hold-label" aria-live="polite">
            {label}
          </p>
        </div>
        <span className="shot" ref={shotEl} aria-hidden="true" />
        {/* the harder cases: the red closing in as it charges */}
        {fx.vignette ? <span className="vignette" aria-hidden="true" /> : null}
      </section>

      {/* the impact, for the harder cases: a flash, shockwaves, sparks */}
      {impact ? (
        <div className="impact-fx" aria-hidden="true">
          {fx.flash ? <span className="flash" /> : null}
          {Array.from({ length: fx.rings }, (_, i) => (
            <span
              key={`r${i}`}
              className="wave"
              style={{ animationDelay: `${i * 130}ms` } as CSSProperties}
            />
          ))}
          {Array.from({ length: fx.sparks }, (_, i) => {
            const a = (i / fx.sparks) * Math.PI * 2;
            const d = 140 + (i % 3) * 60;
            return (
              <span
                key={`s${i}`}
                className="spark"
                style={
                  {
                    "--sdx": `${Math.cos(a) * d}px`,
                    "--sdy": `${Math.sin(a) * d}px`,
                    animationDelay: `${(i % 4) * 25}ms`,
                  } as CSSProperties
                }
              />
            );
          })}
        </div>
      ) : null}

      {/* day: the counterstrike */}
      <section className="day" aria-hidden={!lit} inert={!lit}>
        <div className="day-inner">
          {/* Two panes, one above the other: "Show me examples" pushes the
              mission (and itself) up out of view as the examples rise from
              below with their own button; "Back" brings it down again. */}
          <div className="pane pane-mission" inert={page !== "mission"}>
            <div
              className="day-scroll"
              role="region"
              aria-label="Mission briefing"
              tabIndex={0}
            >
              {/* their strike, as an intercepted transmission: their seal,
            a live dot, the words in quotes */}
              <div className="chip">
                <EnemyMark tone="screen" className="chip-seal" />
                <div>
                  {strikeNotice && (
                    <p className="editor-strike-notice">{strikeNotice}</p>
                  )}
                  <b>
                    <span className="chip-dot" />
                    The Enemy&apos;s Strike
                  </b>
                  <span className="chip-text">{strike}</span>
                </div>
              </div>
              <div className="ours">
                <p className="tag tag-ours">Your Counterstrike</p>
                <h2
                  className={`mission ${long ? "long" : ""}`}
                  style={{ "--mission-size": missionSize } as CSSProperties}
                >
                  {mine.list.map((x, i) => (
                    <span key={i}>
                      <span
                        className="w"
                        style={{ "--d": `${x.d}ms` } as CSSProperties}
                      >
                        {x.w}
                      </span>{" "}
                    </span>
                  ))}
                </h2>
                {detail ? (
                  <p
                    className="detail"
                    style={
                      { "--after": `${mine.end + 200}ms` } as CSSProperties
                    }
                  >
                    {detail}
                  </p>
                ) : null}
              </div>
            </div>
            <div inert={!dock} className={`dock ${dock ? "in" : ""}`}>
              <button
                type="button"
                className="accept"
                onClick={() => {
                  scroller.current?.scrollTo(0, 0);
                  setPage("examples");
                  onPageChange?.("examples");
                }}
              >
                Show me examples
              </button>
            </div>
          </div>
          <div
            className="pane pane-examples"
            inert={page !== "examples" || full !== null}
          >
            <div
              className="day-scroll"
              ref={scroller}
              role="region"
              aria-label="Mission examples"
              tabIndex={0}
            >
              {/* how other agents countered it: their reports, as cards */}
              <div className="examples">
                <button
                  type="button"
                  className="ex-back"
                  onClick={() => {
                    setPage("mission");
                    onPageChange?.("mission");
                  }}
                >
                  ‹ Back to the mission
                </button>
                {mission.customId ? (
                  <div className="ex-head">
                    <p className="tag tag-ours">
                      You could be the example of how to beat this Enemy.
                    </p>
                    <p className="ex-sub">
                      This mission was created just for you, so there aren't any
                      examples of people beating this enemy yet.
                    </p>
                    <p className="ex-sub custom-invite">
                      Win this one, and you'll be the example future agents
                      learn from — if you choose to share it. That choice is
                      yours.
                    </p>
                  </div>
                ) : (
                  <div className="ex-head">
                    <p className="tag tag-ours">
                      {count
                        ? `How ${count} ${count === 1 ? "Agent" : "Agents"} beat this strike.`
                        : "No field reports yet."}
                    </p>
                    <p className="ex-sub">
                      {count
                        ? "Their tactics are yours to steal."
                        : "You can be the first to share an example for this mission."}
                    </p>
                  </div>
                )}
                {reports.map((r, i) => (
                  <ExampleCard
                    key={r.glowId}
                    report={r}
                    index={i}
                    onOpen={() => setFull(i)}
                  />
                ))}
              </div>
            </div>
            <div className="dock in">
              <button type="button" className="accept" onClick={onAccept}>
                {acceptLabel}
              </button>
            </div>
          </div>
          {/* a report, opened as a card over the dimmed screen: all their
              words, and what they sent; a tap outside closes it */}
          {full !== null && reports[full] ? (
            <div
              className="ex-full"
              role="dialog"
              aria-modal="true"
              onClick={() => setFull(null)}
            >
              <div
                className="ex-full-card"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ex-full-bar">
                  <span className="ex-who">
                    {agentName(reports[full].agent)}
                  </span>
                  <button
                    type="button"
                    className="ex-full-close"
                    aria-label="Close"
                    onClick={() => setFull(null)}
                  >
                    ×
                  </button>
                </div>
                <div
                  className="ex-full-body"
                  role="region"
                  aria-label="Full example report"
                  tabIndex={0}
                >
                  <p className="ex-full-text">{reports[full].did}</p>
                  {reports[full].felt ? (
                    <p className="ex-full-text">{reports[full].felt}</p>
                  ) : null}
                  {reports[full].media ? (
                    isVideo(reports[full].media!) ? (
                      <video
                        src={reports[full].media}
                        className="ex-full-media"
                        controls
                        autoPlay
                        playsInline
                      />
                    ) : (
                      <img
                        src={reports[full].media}
                        alt=""
                        className="ex-full-media"
                      />
                    )
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>
      <style>{CSS}</style>
    </div>
  );
}

const CSS = `
.smcs{--ash:#9a9a9a;--dock:96px;
  position:absolute;inset:0;overflow:hidden;background:#000;color:#fff;isolation:isolate;
  user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif}
.smcs button{font:inherit;color:inherit}
.smcs .night{position:absolute;inset:0;display:flex;flex-direction:column;padding:20px 26px 30px}
.smcs .seal{position:absolute;left:50%;top:44%;translate:-50% -50%;pointer-events:none}
.smcs.impact .seal{transition:opacity .4s;opacity:0}
.smcs.lit .night{visibility:hidden}
.smcs .strike{flex:1;display:flex;flex-direction:column;justify-content:center;gap:18px;padding-bottom:4%;will-change:transform}
.smcs .tag{margin:0;font-size:15px;font-weight:600;display:flex;align-items:center;gap:10px}
.smcs .tag::before{content:"";width:28px;height:2px;background:currentColor;transform-origin:left}
.smcs .tag-enemy{color:#ff4a3d;opacity:0;font-size:clamp(18px,5.6vw,21px);font-weight:700;letter-spacing:.01em;white-space:nowrap}
.smcs .tag-enemy::before{display:none}
.smcs .enemy-line{margin:0;font-weight:300;font-size:var(--enemy-size,34px);line-height:1.16;letter-spacing:-.015em}
.smcs .enemy-line .w{display:inline-block;opacity:0;transform:translateY(.25em);filter:blur(6px)}
.smcs.run .tag-enemy{animation:smcsFade .6s .7s forwards}
.smcs.run .tag-enemy::before{animation:smcsDraw .6s .7s both}
.smcs.run .enemy-line .w{animation:smcsWordIn .7s cubic-bezier(.2,.7,.2,1) forwards;animation-delay:var(--d)}
.smcs.skip .tag-enemy,.smcs.skip .tag-enemy::before,.smcs.skip .enemy-line .w{animation-duration:.15s;animation-delay:0s}
.smcs .hold-zone{display:flex;flex-direction:column;align-items:center;gap:12px;opacity:0;transform:translateY(12px);transition:opacity .6s,transform .6s;pointer-events:none}
.smcs.ready .hold-zone{opacity:1;transform:none;pointer-events:auto}
.smcs .hold{position:relative;width:104px;height:104px;border:0;border-radius:50%;background:transparent;padding:0;cursor:pointer;touch-action:none;-webkit-touch-callout:none}
.smcs .cs-ring{position:absolute;inset:0;width:100%;height:100%;transform:rotate(-90deg)}
.smcs .cs-ring circle{fill:none}
.smcs .ring-bg{stroke:rgba(255,255,255,.2);stroke-width:3}
.smcs .ring-fg{stroke:var(--glow);stroke-width:5;stroke-linecap:round;stroke-dasharray:339.3;stroke-dashoffset:calc(339.3 * (1 - var(--p,0)))}
.smcs .pilot{position:absolute;inset:24px;border-radius:50%;background:var(--glow);box-shadow:0 0 calc(18px + var(--p,0) * 70px) calc(2px + var(--p,0) * 26px) color-mix(in srgb,var(--glow) 55%,transparent);transform:scale(calc(.86 + var(--p,0) * .3))}
.smcs.ready:not(.holding) .pilot{animation:smcsBreathe 2.4s ease-in-out infinite}
.smcs .hold-label{margin:0;font-size:20px;font-weight:600;min-height:1.3em;text-align:center}
.smcs .charge{position:absolute;left:50%;top:50%;width:104px;height:104px;border-radius:50%;pointer-events:none;transform:translate(-50%,-50%) scale(calc(1 + var(--p,0) * 3.4 * var(--fx-glow,1)));background:radial-gradient(circle,color-mix(in srgb,var(--glow) calc((.08 + var(--p,0) * .5) * 100%),transparent),color-mix(in srgb,var(--glow) 0%,transparent) 68%)}
.smcs.charged .pilot{animation:smcsThrob .42s ease-in-out infinite;background:var(--flash)}
.smcs.charged .ring-fg{stroke:var(--flash);filter:drop-shadow(0 0 6px var(--glow))}
.smcs.fired .hold-zone{transition:opacity .25s;opacity:0;pointer-events:none}
.smcs .shot{position:absolute;left:var(--sx,50%);top:var(--sy,80%);z-index:3;width:20px;height:20px;border-radius:50%;background:var(--flash);box-shadow:0 0 26px 10px color-mix(in srgb,var(--glow) 90%,transparent);transform:translate(-50%,-50%);display:none;pointer-events:none}
.smcs .shot::after{content:"";position:absolute;left:50%;top:50%;width:8px;height:150px;margin-left:-4px;border-radius:4px;background:linear-gradient(color-mix(in srgb,var(--flash) 95%,transparent),color-mix(in srgb,var(--glow) 0%,transparent))}
.smcs.fired .shot{display:block}
.smcs.impact .shot{display:none}
.smcs.settled .enemy-line .w{animation:none;opacity:1;transform:none;filter:none}
.smcs.impact .enemy-line .w{transition:transform .55s cubic-bezier(.1,.7,.2,1),opacity .5s ease-out;transform:translate(var(--bx),var(--by)) rotate(var(--br));opacity:0}
.smcs.impact .tag-enemy{transition:opacity .3s;opacity:0 !important;animation:none}
.smcs.impact .strike{animation:smcsKick .4s cubic-bezier(.2,.8,.2,1)}
.smcs .vignette{position:absolute;inset:0;pointer-events:none;z-index:1;opacity:calc(var(--p,0) * var(--fx-vignette,0));background:radial-gradient(ellipse at 50% 55%,transparent 30%,rgba(140,0,0,.55) 75%,rgba(60,0,0,.9))}
.smcs.charged .vignette{animation:smcsVig .42s ease-in-out infinite}
.smcs.impact .vignette{transition:opacity .3s;opacity:0}
.smcs .impact-fx{position:absolute;inset:0;z-index:3;pointer-events:none;overflow:hidden}
.smcs .flash{position:absolute;inset:0;background:var(--glow);opacity:0;animation:smcsFlash .5s ease-out both}
.smcs .wave{position:absolute;left:var(--cx);top:var(--cy);width:40px;height:40px;margin:-20px 0 0 -20px;border-radius:50%;border:4px solid var(--glow);box-shadow:0 0 24px var(--glow);opacity:0;animation:smcsWave .8s cubic-bezier(.1,.6,.3,1) both}
.smcs .spark{position:absolute;left:var(--cx);top:var(--cy);width:6px;height:6px;margin:-3px 0 0 -3px;border-radius:50%;background:var(--flash);box-shadow:0 0 10px 3px var(--glow);animation:smcsSpark .7s cubic-bezier(.1,.7,.3,1) both}
.smcs.impact{animation:smcsQuake .55s cubic-bezier(.3,.7,.3,1)}
.smcs .day{position:absolute;inset:0;z-index:2;background:var(--day);color:var(--ink);clip-path:circle(var(--r,0px) at var(--cx,50%) var(--cy,80%));pointer-events:none}
.smcs.lit .day{clip-path:none;pointer-events:auto}
.smcs .day-inner{position:absolute;inset:0;visibility:hidden}
.smcs.lit .day-inner{visibility:visible}
/* the two panes: the examples wait below; "Show me examples" slides both up */
.smcs .pane{position:absolute;inset:0;display:flex;flex-direction:column;min-height:0;overflow:hidden;transition:transform .7s cubic-bezier(.65,0,.25,1)}
.smcs .pane-examples{transform:translateY(100%)}
.smcs.examples-page .pane-mission{transform:translateY(-100%)}
.smcs.examples-page .pane-examples{transform:none}
.smcs .day-scroll{position:relative;flex:1 1 0;min-height:0;min-width:0;overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;touch-action:pan-y;display:flex;flex-direction:column;padding:16px 26px 18px;overflow-wrap:anywhere}
.smcs .day-scroll:focus-visible{outline:2px solid var(--ink);outline-offset:-3px}
.smcs .day-scroll>*{flex-shrink:0}
.smcs.review .day-scroll{padding-top:56px}
.smcs .chip{align-self:stretch;display:flex;align-items:center;gap:12px;position:relative;overflow:hidden;
  background:linear-gradient(160deg,#241a16,#120d0b);
  color:#f4ece4;border-radius:14px;border:1px solid rgba(255,74,61,.35);box-shadow:0 10px 24px rgba(0,0,0,.28);
  padding:12px 16px 13px 14px;font-size:14.5px;line-height:1.4;font-weight:400;opacity:0;transform:translateY(-8px)}
.smcs .chip-seal{flex:none;width:46px;height:46px;opacity:.9}
.smcs .chip-text{font-style:italic}
.smcs .chip-dot{display:inline-block;width:7px;height:7px;margin-right:7px;border-radius:50%;background:#ff4a3d;box-shadow:0 0 8px #ff4a3d;vertical-align:1px;animation:smcsLive 1.4s ease-in-out infinite}
.smcs .chip b{display:block;font-weight:700;font-size:12px;color:#ff6a5c;margin-bottom:3px}
.smcs .detail{margin:12px 0 0;font-size:15px;line-height:1.4;font-weight:500;opacity:0}
.smcs.lit .detail{animation:smcsFade .6s forwards;animation-delay:var(--after)}
.smcs.lit .chip{animation:smcsDrop .5s .1s forwards}
.smcs .ours{margin:0;padding:58px 0 6px;flex:1 0 auto;display:flex;flex-direction:column}
/* the tactics sit at the foot of the page, just over the button */
.smcs .tag-ours{margin-bottom:12px;opacity:0;color:var(--ink);font-size:19px;font-weight:700}
.smcs .tag-ours::before{display:none}
.smcs.lit .tag-ours{animation:smcsFade .4s .25s forwards}
.smcs .mission{margin:0;font-weight:800;font-size:var(--mission-size,34px);line-height:1.12;letter-spacing:-.02em}
.smcs .mission.long{font-weight:700;line-height:1.24;letter-spacing:-.01em}
.smcs .mission .w{display:inline-block;opacity:0;transform:translateY(.4em)}
.smcs.lit .mission .w{animation:smcsWordUp .45s cubic-bezier(.2,.8,.2,1) forwards;animation-delay:var(--d)}
.smcs .dock{position:relative;flex:0 0 var(--dock);height:var(--dock);z-index:4;padding:14px 20px 18px;background:var(--day);transform:translateY(100%);transition:transform .5s cubic-bezier(.2,.8,.2,1)}
.smcs .dock.in{transform:none}
.smcs .accept{width:100%;height:100%;border:0;border-radius:18px;background:#1a1410;color:var(--glow);font-weight:700;font-size:19px;letter-spacing:-.01em;cursor:pointer;transition:transform .15s}
.smcs .accept:active{transform:scale(.97)}
@media (max-height:700px){
  .smcs{--dock:88px}
  .smcs .day-scroll{padding-top:14px}
  .smcs .chip{font-size:13px;padding:9px 12px 10px}
  .smcs .chip-seal{width:38px;height:38px}
  .smcs .ours{padding:40px 0 4px}
  .smcs .enemy-line{font-size:calc(var(--enemy-size,34px) * .9)}
}
@keyframes smcsFade{to{opacity:1}}
@keyframes smcsVig{0%,100%{filter:brightness(1)}50%{filter:brightness(1.5)}}
@keyframes smcsFlash{0%{opacity:var(--fx-flash)}100%{opacity:0}}
@keyframes smcsWave{0%{opacity:1;transform:scale(.4)}100%{opacity:0;transform:scale(14)}}
@keyframes smcsSpark{0%{opacity:1;translate:0 0}100%{opacity:0;translate:var(--sdx) var(--sdy)}}
@keyframes smcsQuake{0%{translate:0 0}15%{translate:calc(var(--fx-quake) * -1) calc(var(--fx-quake) * .6)}30%{translate:var(--fx-quake) calc(var(--fx-quake) * -.5)}45%{translate:calc(var(--fx-quake) * -.6) calc(var(--fx-quake) * .3)}60%{translate:calc(var(--fx-quake) * .4) 0}80%{translate:calc(var(--fx-quake) * -.2) 0}100%{translate:0 0}}
/* page two: how other agents countered it, as cards */
.smcs .examples{display:flex;flex-direction:column;gap:14px;padding-top:4px}
.smcs .ex-back{align-self:flex-start;border:0;background:transparent;padding:4px 0;font:inherit;font-size:14px;font-weight:600;color:var(--ink);opacity:.75;cursor:pointer}
.smcs .examples .tag-ours{opacity:1;margin:0}
.smcs .ex-head{margin-bottom:4px}
/* one line, whatever the phone: it shrinks to fit narrow screens */
.smcs .ex-head .tag-ours{display:block;white-space:normal;overflow-wrap:anywhere;max-width:100%;font-size:19px;line-height:1.35}
.smcs .ex-sub{margin:4px 0 0;font-size:16px;font-weight:500;color:var(--ink);opacity:.8}
.smcs .ex-card{background:var(--paper);color:var(--ink);border-radius:14px;padding:16px 18px;box-shadow:0 10px 26px rgba(0,0,0,.18);
  opacity:0;transform:translateY(18px)}
.smcs.examples-page .ex-card{animation:smcsCardIn .45s cubic-bezier(.2,.8,.3,1) forwards;animation-delay:calc(.45s + var(--i) * .12s)}
.smcs .ex-who{margin:0 0 6px;font-size:13px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;opacity:.7}
.smcs .ex-row{display:flex;gap:14px;align-items:flex-start}
.smcs .ex-body{flex:1;min-width:0}
.smcs .ex-text{margin:0;font-size:16.5px;line-height:1.45;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;line-clamp:3;overflow:hidden}
.smcs .ex-thumb{flex:none;width:110px;height:110px;margin-top:2px;padding:0;border:0;border-radius:10px;overflow:hidden;background:#000;cursor:pointer}
.smcs .ex-thumb img,.smcs .ex-thumb video{display:block;width:100%;height:100%;object-fit:cover}
.smcs .ex-more{margin-top:10px;padding:0;border:0;background:transparent;font:inherit;font-size:14.5px;font-weight:700;color:var(--ink);text-decoration:underline;text-underline-offset:3px;cursor:pointer}
.smcs .ex-full{position:absolute;inset:0;z-index:8;display:grid;grid-template-rows:minmax(0,1fr);place-items:center;padding:20px;background:rgba(0,0,0,.5);animation:smcsFade .25s both;opacity:0}
.smcs .ex-full-card{width:100%;max-width:420px;max-height:100%;min-height:0;display:flex;flex-direction:column;overflow:hidden;background:var(--paper);color:var(--ink);border-radius:16px;box-shadow:0 24px 60px rgba(0,0,0,.4);animation:smcsFullIn .3s cubic-bezier(.2,.8,.3,1) both}
.smcs .ex-full-bar{flex-shrink:0;display:flex;align-items:center;justify-content:space-between;padding:12px 12px 6px 18px}
.smcs .ex-full-bar .ex-who{margin:0}
.smcs .ex-full-close{width:40px;height:40px;border:0;border-radius:50%;background:color-mix(in srgb,var(--ink) 10%,transparent);color:var(--ink);font-size:26px;line-height:1;cursor:pointer}
.smcs .ex-full-body{flex:1;min-height:0;overflow-x:hidden;overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;touch-action:pan-y;overflow-wrap:anywhere;padding:2px 18px 20px}
.smcs .ex-full-body:focus-visible{outline:2px solid var(--ink);outline-offset:-3px}
.smcs .ex-full-text{margin:0;font-size:17px;line-height:1.5;user-select:text;-webkit-user-select:text}
.smcs .ex-full-media{display:block;width:100%;max-height:50vh;margin-top:14px;border-radius:12px;object-fit:contain;background:#000}
@keyframes smcsFullIn{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
@keyframes smcsCardIn{to{opacity:1;transform:none}}
@keyframes smcsLive{0%,100%{opacity:1}50%{opacity:.25}}
@keyframes smcsPop{from{opacity:0;scale:.85}to{opacity:1;scale:1}}
@keyframes smcsDraw{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes smcsWordIn{to{opacity:1;transform:none;filter:blur(0)}}
@keyframes smcsWordUp{to{opacity:1;transform:none}}
@keyframes smcsDrop{to{opacity:1;transform:none}}
@keyframes smcsBreathe{0%,100%{transform:scale(.86)}50%{transform:scale(.96)}}
@keyframes smcsThrob{0%,100%{transform:scale(1.16)}50%{transform:scale(1.32)}}
@keyframes smcsKick{0%{transform:scale(1.035) translateY(-6px)}100%{transform:none}}
@media (prefers-reduced-motion:reduce){
  .smcs .pane{transition:none}
  .smcs.run .tag-enemy,.smcs.run .tag-enemy::before,.smcs.run .enemy-line .w,
  .smcs.lit .mission .w,.smcs.lit .chip,.smcs.lit .tag-ours{animation-duration:.01s;animation-delay:0s}
  .smcs .pilot{animation:none !important}
}
`;
